import { startOfDay } from "date-fns";
import { useMemo, useState } from "react";
import {
	ReminderEntityTypeEnum,
	ReminderStatusEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { ReminderDraftDrawer } from "web_myplann/Reminders/Components/ReminderDraftDrawer";
import { RemindersFlatList } from "web_myplann/Reminders/Components/RemindersFlatList";
import { getReminderEntityConfig } from "web_myplann/Reminders/Config/reminderEntityConfig";
import type { ReminderFormModel } from "web_myplann/Reminders/Forms/reminderFormSchema";
import type { Reminder } from "web_myplann/Reminders/Types/ReminderTypes";
import { hasSelectableTemplates } from "web_myplann/Reminders/Utils/hasSelectableTemplates";
import {
	IDENTIFICATION_DOCUMENT_DRAFT_TEMPLATES,
	type ReminderDraft,
	resolveReminderDrafts,
} from "web_myplann/Reminders/Utils/reminderDrafts";
import { createId } from "web_myplann/Shared/Utils/createId";

const ENTITY_CONFIG = getReminderEntityConfig(
	ReminderEntityTypeEnum.IdentificationDocument,
);

const toReminder = (
	draft: ReminderDraft,
	t: ReturnType<typeof useResource>["t"],
): Reminder => {
	const labelKey = IDENTIFICATION_DOCUMENT_DRAFT_TEMPLATES.find(
		(template) => template.typeKey === draft.model.typeKey,
	)?.labelKey;

	return {
		id: draft.id,
		typeKey: draft.model.typeKey,
		name: labelKey ? t(labelKey) : t(Resources.Reminders.Form.defaultName),
		date: draft.model.date,
		recurrence: draft.model.recurrence,
		status: ReminderStatusEnum.Active,
		snoozedUntil: null,
	};
};

type ActiveDrawer =
	| { type: "none" }
	| { type: "create" }
	| { type: "edit"; draft: ReminderDraft };

type Props = {
	drafts: ReminderDraft[];
	onChange: (drafts: ReminderDraft[]) => void;
	validTo: Date;
	persistedIds?: ReadonlySet<string>;
};

export const ReminderDraftSection = ({
	drafts,
	onChange,
	validTo,
	persistedIds,
}: Props) => {
	const { t } = useResource();

	const [active, setActive] = useState<ActiveDrawer>({ type: "none" });

	const visibleDrafts = useMemo(
		() =>
			resolveReminderDrafts(
				drafts,
				ENTITY_CONFIG,
				validTo,
				startOfDay(new Date()),
			).sort((a, b) => a.model.date.getTime() - b.model.date.getTime()),
		[drafts, validTo],
	);

	const canAdd = hasSelectableTemplates(
		IDENTIFICATION_DOCUMENT_DRAFT_TEMPLATES,
		ENTITY_CONFIG,
		validTo,
	);

	const close = () => setActive({ type: "none" });

	const add = (model: ReminderFormModel) =>
		onChange([...drafts, { id: createId(), model, validTo }]);

	const update = (id: string, model: ReminderFormModel) =>
		onChange(
			drafts.map((draft) => (draft.id === id ? { id, model, validTo } : draft)),
		);

	const remove = (id: string) =>
		onChange(drafts.filter((draft) => draft.id !== id));

	return (
		<>
			<RemindersFlatList
				reminders={visibleDrafts.map((draft) => toReminder(draft, t))}
				canAdd={canAdd}
				onAdd={() => setActive({ type: "create" })}
				onTap={({ id }) => {
					const draft = drafts.find((item) => item.id === id);
					if (draft) setActive({ type: "edit", draft });
				}}
				onRemove={({ id }) => remove(id)}
				lockedIds={persistedIds}
			/>

			{active.type === "create" && (
				<ReminderDraftDrawer validTo={validTo} onSave={add} onClose={close} />
			)}

			{active.type === "edit" && (
				<ReminderDraftDrawer
					validTo={validTo}
					draft={active.draft}
					onSave={(model) => update(active.draft.id, model)}
					onClose={close}
				/>
			)}
		</>
	);
};
